import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';
import {
  IActivityLog,
  IAddToolRequest,
  IAddToolResponse,
  IApmDetails,
  IConfigData,
  ICreateAdminRequest,
  IAdmin,
  IGetApmsResponse,
  IGetProjectTools,
  IConfigOptionsResponse,
} from '../components/tools-provisioning/interfaces';
import { IPTRun } from '../components/tools-provisioning/interfaces';
import { IqeProjectOperationRequest } from '../components/tools-provisioning/components/IQEAgent/interfaces/api/requests/saveProject.request';
import type { IIqeRunResultsRequest } from '../components/tools-provisioning/components/IQEAgent/interfaces/api/requests/runResults.request';
import type {
  IExecutionDashboardResponse,
  ITestPlanResponse,
} from '../components/tools-provisioning/components/IQEAgent/interfaces/api/responses/runResults.response';
import { toIqeRunResultsUrl } from '../components/tools-provisioning/components/IQEAgent/dto/requestDto/runResults.request.dto';

export interface ToolsProvisioningApi {
  getApms: () => Promise<IGetApmsResponse>;
  getApmInfoById: (apmNumber: string) => Promise<IApmDetails>;
  // Tools Management
  addToolToProject: (tools: IAddToolRequest) => Promise<IAddToolResponse>;
  getProjectTools: (apmNumber: string) => Promise<IGetProjectTools>;
  getActivityLog: (apmNumber: string) => Promise<IActivityLog[]>;

  // Project mapping APIs
  getAllServicesMapping: (apmNumber: string) => Promise<any>;

  // Admin Apis
  createAdmin: (data: ICreateAdminRequest) => Promise<IAdmin>;
  deleteAdmin: (id: string) => Promise<any>;
  getAllAdmins: (apmNumber: string) => Promise<IAdmin[]>;
  getAmpKeysFromEmail: (email: string) => Promise<string[]>;

  // Performace Test Related Apis
  getConfigOptions: () => Promise<IConfigOptionsResponse>;
  savePTConfig: (config: any, validate?: boolean) => Promise<any>;
  updatePTConfig: (config: any, validate?: boolean) => Promise<any>;
  deletePTConfig: (configId: string) => Promise<any>;
  runPTConfig: (configId: string) => Promise<any>;
  validatePTConfig: (configId: string) => Promise<any>;
  getPTConfigs: (apmNumber: string) => Promise<IConfigData[] | undefined>;
  // Performace Test - Runs
  getPTRuns: (apmNumber: string) => Promise<IPTRun[]>;
  stopPTRun: (immutableId: string) => Promise<any>;
  downloadRunResults: (immutableId: string) => Promise<any>;
  getStatus: (immutableId: string) => Promise<any>;

  /* Performance Test - SLA */
  getSLAList: (apmNumber: string) => Promise<any>;
  addSLA: (data: any, slaId?: string) => Promise<any>;
  addEditTransaction: (data: any, slaId: string, type?: string) => Promise<any>;

  // Planner QE Agent
  getProjects: (apmNumber: string) => Promise<any>;
  getProvisionedProjectDetails: (
    apmNumber: string,
    service: string,
  ) => Promise<any>;
  getProvisionedTools: (apmNumber: string, tool: string) => Promise<any>;
  updateAcceptanceCriteriaFormat: (settings: any) => Promise<any>;

  // QE Score
  triggerQeScoreAnalysis: (apmNumber: string) => Promise<any>;
  getQeScoreTrend: (apmNumber: string, apiRequestId: string) => Promise<any>;
  getQeScoreAnalysis: (apmNumber: string, apiRequestId: string) => Promise<any>;
  getQeAnalysisWorklogStream: (
    apmNumber: string,
    apiRequestId: string,
    accessToken: string,
  ) => Promise<any>;
  getQeScoreAnalysisReport: (
    apmNumber: string,
    apiRequestId: string,
  ) => Promise<Blob>;
  getQeScoreAnalysisStatus: (
    apmNumber: string,
    apiRequestId: string,
  ) => Promise<any>;

  // IQE Agent

  getIqeProjects: (apmNumber: string) => Promise<any>;
  getIqeProjectStatus: (
    apmNumber: string,
    iqeProjectId: string,
  ) => Promise<any>;

  getAdoProjectTestPlans: (
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectOrg: string,
  ) => Promise<any>;

  getJiraProjectTestPlans: (
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectKey: string,
  ) => Promise<any>;

  getAdoProjectTestSuites: (
    apmNumber: string,
    projectName: string,
    projectOrg: string,
    testPlanId: string,
    testPlanName: string,
  ) => Promise<any>;

  performProjectOperation: (
    apmNumber: string,
    iqeProjectData: IqeProjectOperationRequest,
  ) => Promise<any>;
  getIqeActivityLogStream: (
    request: IIqeRunResultsRequest,
    accessToken: string,
    signal?: AbortSignal,
  ) => Promise<Response>;
  getIqeExecutionDashboard: (
    request: IIqeRunResultsRequest,
  ) => Promise<IExecutionDashboardResponse>;
  getIqeTestPlan: (
    request: IIqeRunResultsRequest,
  ) => Promise<ITestPlanResponse>;
}

export const toolsProvisioningApiRef = createApiRef<ToolsProvisioningApi>({
  id: 'cbre.devx.api.toolsprovisioning',
});

export class ToolsProvisioningApiImpl implements ToolsProvisioningApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async getApms(): Promise<IGetApmsResponse> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/registration/apminfo`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting repo info ${err.message}`,
        );
      });
  }

  async getApmInfoById(apmNumber: string): Promise<IApmDetails> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/registration/apminfo/${apmNumber}`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting repo info ${err.message}`,
        );
      });
  }

  /* Tools Management Services */

  /* Add Tool to project */
  async addToolToProject(tool: IAddToolRequest): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/tool`,
        data: { ...tool },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while adding tool ${err.message}`);
      });
  }

  /* get tools of the project */
  async getProjectTools(apmNumber: string): Promise<IGetProjectTools> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/provision/tool/status/${apmNumber}`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting tools status ${err.message}`,
        );
      });
  }

  /* get project mapping */
  async getAllServicesMapping(apmNumber: string): Promise<IActivityLog[]> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/productmapping/${apmNumber}/any/any`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting mapped services ${err.message}`,
        );
      });
  }

  /* get tool Activity log */
  async getActivityLog(apmNumber: string): Promise<IActivityLog[]> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/provision/tool/activitylog/${apmNumber}`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting tools status ${err.message}`,
        );
      });
  }

  /* Create Admin for Application/Project */
  async createAdmin(adminObj: ICreateAdminRequest): Promise<IAdmin> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/registration/admin`,
        data: { ...adminObj },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while creating admin ${err.message}`);
      });
  }

  /* Delete Admin for Application/Project */
  async deleteAdmin(id: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'delete',
        url: `service/registration/admin/${id}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while deleting admin ${err.message}`);
      });
  }

  /* Get all admins of Application/Project */
  async getAllAdmins(apmNumber: string): Promise<IAdmin[]> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/registration/admin/apminfo/${apmNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting admins ${err.message}`);
      });
  }

  /* Trigger QE Score Analysis for Application/Project */
  async triggerQeScoreAnalysis(apmNumber: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/qe/score/analyze`,
        data: { apmNumber },
      })
      .then(res => res?.data)
      .catch(err => {
        if (err?.response?.data?.error) {
          if (err.response.status === 409)
            throw new Error(
              err.response.data.message ||
                'QE Score Analysis is already in progress for this application',
            );
          else
            throw new Error(
              `Error while triggering QE Score Analysis ${err.message}`,
            );
        }
      });
  }

  /* Get QE Score for Application/Project */
  async getQeScoreTrend(apmNumber: string, apiRequestId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/qe/score/${apmNumber}/${apiRequestId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        const apiErrorMessage =
          err?.response?.data?.error ??
          err?.response?.data?.message ??
          err?.message;
        throw new Error(`Error while getting QE Score ${apiErrorMessage}`);
      });
  }

  async getQeScoreAnalysis(
    apmNumber: string,
    apiRequestId: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/qe/score/analysis/${apmNumber}/${apiRequestId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting QE Score Analysis ${err.message}`);
      });
  }

  async getQeAnalysisWorklogStream(
    apmNumber: string,
    apiRequestId: string,
    accessToken: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    const baseUrl = instance.getUri();
    const streamUrl = `${
      baseUrl.slice(-1) === '/' ? baseUrl : `${baseUrl}/`
    }service/provision/qe/score/analysis/${apmNumber}/${apiRequestId}/logs`;

    return fetch(streamUrl, {
      method: 'GET',
      headers: {
        'api-version': '1.0',
        'x-token': 'devx',
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(res => {
      if (!res.ok) {
        const streamError = new Error(
          `Error while getting QE Score Analysis logs ${res.status} ${res.statusText}`,
        ) as Error & {
          statusCode?: number;
          responseHeaders?: Headers;
        };

        streamError.statusCode = res.status;
        streamError.responseHeaders = res.headers;

        throw streamError;
      }

      return res;
    });
  }

  /* Get QE Score Analysis Report for Application/Project */
  async getQeScoreAnalysisReport(
    apmNumber: string,
    apiRequestId: string,
  ): Promise<Blob> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/qe/score/analysis/${apmNumber}/${apiRequestId}/report`,
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      })
      .then(res => res?.data as Blob)
      .catch(err => {
        throw new Error(
          `Error while getting QE Score Analysis Report ${err.message}`,
        );
      });
  }

  async getQeScoreAnalysisStatus(
    apmNumber: string,
    apiRequestId: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/qe/score/status/${apmNumber}/${apiRequestId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting QE Score Analysis Status ${err.message}`,
        );
      });
  }

  // IQE Agent

  async getJiraProjectTestPlans(
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectKey: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `/service/iqe/jira/${apmNumber}/testplans`,
        data: {
          projectid: projectId,
          projectname: projectName,
          projectkey: projectKey,
        },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test plans ${err.message}`,
        );
      });
  }

  async getIqeProjects(apmNumber: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/iqe/${apmNumber}/projects`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting APM projects ${err.message}`);
      });
  }

  async getAdoProjectTestPlans(
    apmNumber: string,
    projectId: string,
    projectName: string,
    projectOrg: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/ado/${apmNumber}/testplans`,
        data: {
          projectid: projectId,
          projectname: projectName,
          projectorg: projectOrg,
        },
      })

      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test plans ${err.message}`,
        );
      });
  }

  async getAdoProjectTestSuites(
    apmNumber: string,
    projectName: string,
    projectOrg: string,
    testPlanId: string,
    testPlanName: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/ado/${apmNumber}/testsuites`,
        data: {
          organization: projectOrg,
          project: projectName,
          testPlanId: testPlanId,
          testPlanName: testPlanName,
        },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting project test suites ${err.message}`,
        );
      });
  }

  // operation is set in the request payload
  async performProjectOperation(
    apmNumber: string,
    iqeProjectData: IqeProjectOperationRequest,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;

    return instance
      .request({
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
        url: `service/iqe/${apmNumber}/project`,
        data: { ...iqeProjectData },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while performing IQE project ${iqeProjectData.operation} ${err.message}`,
        );
      });
  }

  async getIqeActivityLogStream(
    request: IIqeRunResultsRequest,
    accessToken: string,
    signal?: AbortSignal,
  ): Promise<Response> {
    const instance = await this.axiosInstancePromise;
    const url = instance.getUri({ url: toIqeRunResultsUrl(request) });

    const response = await fetch(url, {
      method: 'GET',
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'api-version': '1.0',
        'x-token': 'devx',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Error while getting IQE activity log ${response.status} ${response.statusText}`,
      );
    }

    return response;
  }

  async getIqeExecutionDashboard(
    request: IIqeRunResultsRequest,
  ): Promise<IExecutionDashboardResponse> {
    const instance = await this.axiosInstancePromise;
    const url = toIqeRunResultsUrl(request);
    return instance
      .get(url)
      .then(response => response.data as IExecutionDashboardResponse)
      .catch(err => {
        throw new Error(
          err?.response?.data?.message ||
            `Error while getting IQE execution results ${err.message}`,
        );
      });
  }

  async getIqeTestPlan(
    request: IIqeRunResultsRequest,
  ): Promise<ITestPlanResponse> {
    const instance = await this.axiosInstancePromise;
    const url = toIqeRunResultsUrl(request);
    return instance
      .get(url)
      .then(response => response.data as ITestPlanResponse)
      .catch(err => {
        throw new Error(
          err?.response?.data?.message ||
            `Error while getting IQE test plan ${err.message}`,
        );
      });
  }

  async getIqeProjectStatus(
    apmNumber: string,
    iqeProjectId: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/iqe/${apmNumber}/project/${iqeProjectId}/status`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error while getting IQE project status ${err.message}`,
        );
      });
  }

  /* Get amp keys from email */
  async getAmpKeysFromEmail(email: string): Promise<string[]> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/registration/admin/${email}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error Apm keys for the email ${err.message}`);
      });
  }

  /* Get config options of Application/Project */
  async getConfigOptions(): Promise<IConfigOptionsResponse> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/options`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting config options ${err.message}`);
      });
  }

  /* Save Config for Performace Test */
  async savePTConfig(config: any, validate?: boolean): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/performancetest/config${
          validate ? '?validate=true' : ''
        }`,
        data: { ...config },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while saving config ${err.message}`);
      });
  }

  /* Update Config for Performace Test */
  async updatePTConfig(config: any, validate?: boolean): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'put',
        url: `service/provision/performancetest/config${
          validate ? '?validate=true' : ''
        }`,
        data: { ...config },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while updating config ${err.message}`);
      });
  }

  /* delete Config for Performace Test */
  async deletePTConfig(configId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'delete',
        url: `service/provision/performancetest/config/${configId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while deleteing config ${err.message}`);
      });
  }

  /* run Config for Performace Test */
  async runPTConfig(configId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/performancetest/run/${configId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while running config ${err.message}`);
      });
  }

  // Validate existing config
  async validatePTConfig(configId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/performancetest/config/${configId}/validate`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while validating config ${err.message}`);
      });
  }

  /* Get all configs for Performance Test */
  async getPTConfigs(apmNumber: string): Promise<IConfigData[] | undefined> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/config/apm/${apmNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting configs ${err.message}`);
      });
  }

  /* Get all runs for Performance Test */
  async getPTRuns(apmNumber: string): Promise<IPTRun[]> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/runs/${apmNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting runs ${err.message}`);
      });
  }

  /* stop run for Performace Test */
  async stopPTRun(immutableId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/provision/performancetest/run/stop/${immutableId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while stopping run ${err.message}`);
      });
  }
  /* download run results for Performace Test */
  async downloadRunResults(immutableId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/run/results/${immutableId}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while downloading run results ${err.message}`);
      });
  }

  async getStatus(immutableId: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/run/results/${immutableId}`,
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while getting status ${err.message}`);
      });
  }

  /* Performance Test - SLA */
  async getSLAList(apmNumber: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/performancetest/sla/apm/${apmNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting sla list ${err.message}`);
      });
  }

  async addSLA(sla: any, slaId?: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: slaId ? 'put' : 'post',
        url:
          'service/provision/performancetest/sla' + (slaId ? `/${slaId}` : ''),
        data: { ...sla },
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while adding SLA ${err.message}`);
      });
  }

  async addEditTransaction(
    transaction: any,
    slaId: string,
    type?: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: type === 'edit' ? 'put' : 'post',
        url: `service/provision/performancetest/sla/${slaId}/transactions`,
        data: { ...transaction },
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while updating transaction ${err.message}`);
      });
  }

  async getProjects(apmNumber: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/apm/${apmNumber}/jira`,
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while getting status ${err.message}`);
      });
  }

  async getProvisionedProjectDetails(
    apmNumber: string,
    service: string,
  ): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/productmapping/${apmNumber}/${
          service === 'jira' ? 'JIRA' : 'ADO'
        }/Project`,
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while getting status ${err.message}`);
      });
  }

  async getProvisionedTools(apmNumber: string, tool: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/provision/tool/status/${apmNumber}/${
          tool === 'jira'
            ? 'enable-jiraPlannerBot'
            : tool === 'sonarqube'
            ? 'enable-sonarqube-agents'
            : tool === 'devops'
            ? 'enable-devops-agents'
            : 'enable-ADOPlannerBot'
        }/any`,
      })
      .then(res => res)
      .catch(err => {
        throw new Error(`Error while getting status ${err.message}`);
      });
  }

  async updateAcceptanceCriteriaFormat(settings: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: 'service/provision/tool/custom-settings/upsert',
        data: { ...settings },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while updating format ${err.message}`);
      });
  }
}
